/**
 * UTM parameter tracking utilities for GA4 attribution.
 * Captures and persists UTM parameters for backend event tracking.
 */

interface UTMParameters {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const UTM_STORAGE_KEY = "utm_params";
const UTM_EXPIRY_DAYS = 30; // Keep UTM params for 30 days

/**
 * Extract UTM parameters from URL.
 */
export function extractUTMParams(): UTMParameters {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(window.location.search);
  const utm: UTMParameters = {};

  // Extract standard UTM parameters
  if (params.get("utm_source")) utm.utm_source = params.get("utm_source")!;
  if (params.get("utm_medium")) utm.utm_medium = params.get("utm_medium")!;
  if (params.get("utm_campaign")) utm.utm_campaign = params.get("utm_campaign")!;
  if (params.get("utm_term")) utm.utm_term = params.get("utm_term")!;
  if (params.get("utm_content")) utm.utm_content = params.get("utm_content")!;

  return utm;
}

/**
 * Get referrer-based attribution if no UTM params.
 */
export function getReferrerAttribution(): UTMParameters {
  if (typeof window === "undefined" || !document.referrer) return {};

  try {
    const referrer = new URL(document.referrer);
    const currentHost = window.location.hostname;

    // Skip if referrer is same domain
    if (referrer.hostname === currentHost) return {};

    // Determine source and medium from referrer
    const hostname = referrer.hostname.toLowerCase();
    const utm: UTMParameters = {};

    // Common referrer patterns
    if (hostname.includes("google.")) {
      utm.utm_source = "google";
      utm.utm_medium = "organic";
    } else if (hostname.includes("facebook.") || hostname.includes("fb.")) {
      utm.utm_source = "facebook";
      utm.utm_medium = "social";
    } else if (hostname.includes("twitter.") || hostname === "t.co") {
      utm.utm_source = "twitter";
      utm.utm_medium = "social";
    } else if (hostname.includes("linkedin.")) {
      utm.utm_source = "linkedin";
      utm.utm_medium = "social";
    } else if (hostname.includes("reddit.")) {
      utm.utm_source = "reddit";
      utm.utm_medium = "social";
    } else if (hostname.includes("github.")) {
      utm.utm_source = "github";
      utm.utm_medium = "referral";
    } else {
      // Generic referral
      utm.utm_source = hostname;
      utm.utm_medium = "referral";
    }

    return utm;
  } catch {
    return {};
  }
}

/**
 * Store UTM parameters in localStorage with expiry.
 */
export function storeUTMParams(params: UTMParameters): void {
  if (typeof window === "undefined" || Object.keys(params).length === 0) return;

  const data = {
    params,
    timestamp: Date.now(),
    expiry: Date.now() + UTM_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
  };

  localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(data));
}

/**
 * Retrieve stored UTM parameters if not expired.
 */
export function getStoredUTMParams(): UTMParameters {
  if (typeof window === "undefined") return {};

  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) return {};

    const data = JSON.parse(stored);

    // Check expiry
    if (Date.now() > data.expiry) {
      localStorage.removeItem(UTM_STORAGE_KEY);
      return {};
    }

    return data.params || {};
  } catch {
    return {};
  }
}

/**
 * Capture and store UTM parameters on page load.
 * Should be called early in the app lifecycle.
 */
export function captureUTMParams(): UTMParameters {
  // First check for UTM params in URL
  let params = extractUTMParams();

  // If no UTM params, check for referrer attribution
  if (Object.keys(params).length === 0) {
    params = getReferrerAttribution();
  }

  // Store if we found any params
  if (Object.keys(params).length > 0) {
    storeUTMParams(params);
    return params;
  }

  // Otherwise return stored params
  return getStoredUTMParams();
}

/**
 * Get current attribution data for sending to backend.
 * Combines fresh URL params with stored params.
 */
export function getCurrentAttribution(): UTMParameters {
  // Check for fresh UTM params first
  const urlParams = extractUTMParams();
  if (Object.keys(urlParams).length > 0) {
    storeUTMParams(urlParams);
    return urlParams;
  }

  // Fall back to stored params
  return getStoredUTMParams();
}

/**
 * Build an OAuth URL with UTM query parameters appended.
 * Reads current attribution and appends non-empty UTM params.
 */
export function buildOAuthUrl(basePath: string): string {
  const utm = getCurrentAttribution();
  const params = new URLSearchParams();

  if (utm.utm_source) params.set("utm_source", utm.utm_source);
  if (utm.utm_medium) params.set("utm_medium", utm.utm_medium);
  if (utm.utm_campaign) params.set("utm_campaign", utm.utm_campaign);
  if (utm.utm_term) params.set("utm_term", utm.utm_term);
  if (utm.utm_content) params.set("utm_content", utm.utm_content);

  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}

/**
 * Clear stored UTM parameters.
 */
export function clearUTMParams(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem(UTM_STORAGE_KEY);
  }
}
